/**
 * Edge Hover Info Component
 *
 * Displays flow metadata in a tooltip when hovering over an edge.
 * Shows flow type, label, and direction information.
 * Note: x,y are in EdgeLabelRenderer coordinate space (flow coordinates with transform applied).
 */
import { memo, useState, useEffect } from "react";

interface EdgeHoverInfoProps {
  /** Flow type (value, signal, internal) */
  flowType: "value" | "signal" | "internal";
  /** Optional flow label */
  label?: string;
  /** Whether hover is active */
  isHovered: boolean;
  /** Position for the tooltip (in EdgeLabelRenderer coordinate space) */
  x: number;
  y: number;
  /** Delay before showing (ms) */
  delay?: number;
}

/**
 * Get human-readable flow type description
 */
const getFlowTypeDescription = (flowType: string): { label: string; description: string } => {
  switch (flowType) {
    case "signal":
      return {
        label: "Signal Flow",
        description: "Control or data signal between components",
      };
    case "value":
      return {
        label: "Value Flow",
        description: "Transfer of value or assets",
      };
    case "internal":
      return {
        label: "Internal Flow",
        description: "Internal redistribution or allocation",
      };
    default:
      return {
        label: "Flow",
        description: "Connection between components",
      };
  }
};

/**
 * Get flow type color
 */
const getFlowTypeColor = (flowType: string): string => {
  switch (flowType) {
    case "signal":
      return "text-purple-400";
    case "value":
      return "text-sa-success";
    case "internal":
      return "text-cyan-400";
    default:
      return "text-sa-text-dim";
  }
};

export const EdgeHoverInfo = memo(
  ({ flowType, label, isHovered, x, y, delay = 300 }: EdgeHoverInfoProps) => {
    const [showTooltip, setShowTooltip] = useState(false);

    useEffect(() => {
      if (isHovered) {
        const timer = setTimeout(() => setShowTooltip(true), delay);
        return () => clearTimeout(timer);
      } else {
        setShowTooltip(false);
      }
    }, [isHovered, delay]);

    if (!showTooltip) {
      return null;
    }

    const { label: typeLabel, description } = getFlowTypeDescription(flowType);
    const colorClass = getFlowTypeColor(flowType);

    // Position using transform from EdgeLabelRenderer coordinate space
    return (
      <div
        style={{
          position: "absolute",
          transform: `translate(-50%, -100%) translate(${x}px, ${y - 10}px)`,
          pointerEvents: "none",
          zIndex: 9999,
        }}
        className="animate-in fade-in-0 zoom-in-95 duration-150"
      >
        <div
          className={`
            bg-sa-surface/95
            border border-sa-border
            rounded-lg shadow-xl
            px-3 py-2
            min-w-[140px] max-w-[220px]
            mb-2
          `}
        >
          {/* Flow type badge */}
          <div className={`text-xs font-semibold uppercase tracking-wide ${colorClass} mb-1`}>
            {typeLabel}
          </div>

          {/* Label if present */}
          {label && (
            <div className="text-sm text-sa-text font-medium mb-1 break-words">{label}</div>
          )}

          {/* Description */}
          <div className="text-[11px] text-sa-text-dim leading-snug">{description}</div>
        </div>
      </div>
    );
  },
);

EdgeHoverInfo.displayName = "EdgeHoverInfo";
